import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Role } from '@nexa/auth';
import { RoleGate } from '@/auth/role-gate';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import {
  useForceAcceptAllDisruption,
  useTriggerAllocate,
} from '@/lib/disruptions/use-disruptions';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { PnrSourceMark } from '@/components/provenance/pnr-source-mark';
import { priorityShortLabel } from '../labels';
import type { DeskRow } from './desk-model';
import { ResolvePanel } from './resolve-panel';

// The desk's center, shaped like an INBOX: the queue on the left is a compact,
// buttonless list — the whole row selects; solving happens ONCE, with context,
// in the resolve panel on the right (offers + the alternatives ladder). The
// first open PNR is auto-selected and resolving auto-advances to the next, so
// working the queue to zero is a flow, not 13 identical button rows. The
// awaiting-reply tail is a collapsed strip — visible, never in the way.

export function WorkQueue({
  queue,
  disruptionUrn,
}: {
  queue: DeskRow[];
  disruptionUrn: string;
}) {
  const { t } = useTranslation('tower');
  // Actionable = the operator can do something NOW. Waiting = the passenger's turn.
  const actionable = useMemo(
    () => queue.filter((d) => d.lane === 'NEEDS_HOTEL' || d.lane === 'DECLINED'),
    [queue],
  );
  const waiting = useMemo(() => queue.filter((d) => d.lane === 'AWAITING_REPLY'), [queue]);

  const [selectedUrn, setSelectedUrn] = useState<string | null>(null);
  const selected =
    actionable.find((d) => d.row.subCaseUrn === selectedUrn) ?? actionable[0] ?? null;

  // Auto-advance: when the selected PNR leaves the queue (held / outcome recorded),
  // selection falls through to the next open one via the ?? above; clearing the
  // stored urn keeps the fallback in charge.
  useEffect(() => {
    if (selectedUrn && !actionable.some((d) => d.row.subCaseUrn === selectedUrn)) {
      setSelectedUrn(null);
    }
  }, [selectedUrn, actionable]);

  const allocate = useTriggerAllocate(disruptionUrn);
  const forceAccept = useForceAcceptAllDisruption(disruptionUrn);
  const [waitingOpen, setWaitingOpen] = useState(false);

  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[minmax(15rem,2fr)_minmax(0,3fr)]">
      <div className="flex min-h-0 flex-col gap-2" data-testid="hotel-work-queue">
        <div className="flex items-center justify-between gap-2 px-1">
          <p className="text-sm font-semibold">
            {t('desk.queue.toPlace')}
            <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-[11px] font-bold tabular-nums text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              {actionable.length}
            </span>
          </p>
          {actionable.length > 1 ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => allocate.mutate()}
              disabled={allocate.isPending}
              data-testid="queue-allocate-all"
              title={t('desk.queue.autoAllocateAllTitle')}
              className="text-xs text-muted-foreground"
            >
              {allocate.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : null}
              {t('desk.queue.autoAllocateAll')}
            </Button>
          ) : null}
        </div>

        <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
          {actionable.map((d) => (
            <QueueRow
              key={d.row.subCaseUrn}
              desk={d}
              selected={selected?.row.subCaseUrn === d.row.subCaseUrn}
              onSelect={() => setSelectedUrn(d.row.subCaseUrn)}
            />
          ))}
          {actionable.length === 0 ? (
            <li className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
              {t('desk.queue.empty')}
            </li>
          ) : null}
        </ul>

        {waiting.length > 0 ? (
          <section className="rounded-lg border border-border bg-card" data-testid="waiting-strip">
            <button
              type="button"
              onClick={() => setWaitingOpen((v) => !v)}
              aria-expanded={waitingOpen}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
              data-testid="waiting-strip-toggle"
            >
              <p className="text-sm">
                <span aria-hidden className="mr-1.5 inline-block h-2 w-2 rounded-full bg-sky-500" />
                <span className="font-semibold tabular-nums">{waiting.length}</span>{' '}
                <span className="text-muted-foreground">{t('desk.queue.waiting')}</span>
              </p>
              <ChevronDown
                className={cn('h-4 w-4 text-muted-foreground transition-transform', waitingOpen && 'rotate-180')}
                aria-hidden
              />
            </button>
            {waitingOpen ? (
              <div className="border-t border-border px-3 py-2">
                <p
                  className="max-h-24 overflow-y-auto text-xs leading-5 text-muted-foreground"
                  data-testid="waiting-strip-pnrs"
                >
                  {waiting.map((d, i) => (
                    <span key={d.row.subCaseUrn}>
                      {i > 0 ? ' · ' : null}
                      {d.row.pnrLabel}
                      <PnrSourceMark
                        source={d.row.pnrSource}
                        className="ml-1"
                        testId="waiting-strip-source"
                      />
                    </span>
                  ))}
                </p>
                <RoleGate min={Role.OPS_SUPERVISOR}>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={() => forceAccept.mutate()}
                    disabled={forceAccept.isPending}
                    data-testid="queue-force-accept"
                    title={t('desk.queue.forceAcceptTitle')}
                  >
                    {forceAccept.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                    ) : null}
                    {t('desk.queue.forceAccept')}
                  </Button>
                </RoleGate>
              </div>
            ) : null}
          </section>
        ) : null}
      </div>

      {selected ? (
        <ResolvePanel
          key={selected.row.subCaseUrn}
          desk={selected}
          disruptionUrn={disruptionUrn}
        />
      ) : (
        <div className="hidden items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground lg:flex">
          {waiting.length > 0 ? t('desk.queue.allSet') : t('desk.queue.emptyQueue')}
        </div>
      )}
    </div>
  );
}

/** One queue row — a single line, the whole row is the button. */
function QueueRow({
  desk,
  selected,
  onSelect,
}: {
  desk: DeskRow;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation('tower');
  const { row, lane } = desk;
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        data-testid={`queue-row-${row.pnrLabel}`}
        className={cn(
          'flex w-full items-center gap-2 rounded-md border px-2.5 py-1.5 text-left transition-colors',
          selected
            ? 'border-primary bg-primary/5'
            : 'border-transparent hover:border-border hover:bg-muted/50',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'h-2 w-2 shrink-0 rounded-full',
            lane === 'DECLINED' ? 'bg-rose-500' : 'bg-amber-500',
          )}
          title={
            lane === 'DECLINED' ? t('desk.lane.declined') : t('desk.queue.rowNeedsHotel')
          }
        />
        <span className="font-mono text-sm font-semibold">{row.pnrLabel}</span>
        <PnrSourceMark source={row.pnrSource} className="shrink-0" testId="queue-row-source" />
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {t('desk.queue.rowPax', { pax: row.paxCount, flight: row.flightLabel })}
        </span>
        {(row.priorityTags ?? []).slice(0, 2).map((tag) => (
          <span
            key={tag}
            className="shrink-0 rounded bg-violet-100 px-1 py-px text-[10px] font-semibold text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"
          >
            {priorityShortLabel(tag)}
          </span>
        ))}
      </button>
    </li>
  );
}

export type { DisruptionPnrGroupRow };
